import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Check, Menu, X, ArrowRight, ExternalLink, Shield, Zap, Webhook as WebhookIcon, Key } from 'lucide-react';

// ────────────────────────────────────────────────────────────────────────────
// Public-facing developer docs for the BrixLead Public API + Webhooks.
// Lives at /docs/api (NO auth required) so vendors can link to it from
// their internal wikis, Zapier app description, etc. without needing a
// BrixLead session.
//
// Self-contained single file — easier to maintain than a multi-page MDX
// site for our scale. If this grows past ~1500 lines we should split it.
// ────────────────────────────────────────────────────────────────────────────

const SECTIONS = [
  { id: 'introduction',    label: 'Introduction',     icon: Zap },
  { id: 'authentication',  label: 'Authentication',   icon: Key },
  { id: 'rate-limits',     label: 'Rate Limits',      icon: Shield },
  { id: 'endpoints',       label: 'API Endpoints',    icon: ArrowRight },
  { id: 'lead-object',     label: 'Lead Object',      icon: ArrowRight },
  { id: 'webhooks',        label: 'Webhooks',         icon: WebhookIcon },
  { id: 'webhook-events',  label: 'Webhook Events',   icon: ArrowRight },
  { id: 'webhook-verify',  label: 'Verifying Signatures', icon: Shield },
  { id: 'webhook-retries', label: 'Retry Policy',     icon: ArrowRight },
  { id: 'errors',          label: 'Errors',           icon: ArrowRight },
  { id: 'changelog',       label: 'Changelog',        icon: ArrowRight },
];

type Lang = 'curl' | 'node' | 'python';

export function ApiDocsPage() {
  const [activeSection, setActiveSection] = useState('introduction');
  const [navOpen, setNavOpen] = useState(false);
  const [lang, setLang] = useState<Lang>('curl');

  // Highlight the section currently in the viewport (sticky-side TOC sync).
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) setActiveSection(visible[0].target.id);
      },
      { rootMargin: '-20% 0px -70% 0px' },
    );
    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setNavOpen(false);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setNavOpen(true)}
              className="lg:hidden p-1.5 hover:bg-slate-100 rounded"
              aria-label="Open navigation"
            >
              <Menu className="h-5 w-5 text-slate-600" />
            </button>
            <Link to="/" className="font-bold text-[#6366F1] text-lg">BrixLead</Link>
            <span className="text-xs text-slate-400 hidden sm:inline">/ API Docs</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Link to="/login" className="text-slate-600 hover:text-slate-900">Sign in</Link>
            <Link to="/register" className="text-white bg-[#6366F1] hover:bg-[#4F46E5] px-3 py-1.5 rounded-lg font-medium">
              Get API key
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 lg:flex lg:gap-8">
        {/* Sidebar nav */}
        <nav
          className={`fixed lg:sticky inset-0 lg:inset-auto lg:top-20 z-40 lg:z-0 lg:w-56 lg:flex-shrink-0 lg:h-[calc(100vh-6rem)] overflow-y-auto bg-white lg:bg-transparent transition-transform ${
            navOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0`}
        >
          <div className="lg:hidden flex justify-end p-4">
            <button onClick={() => setNavOpen(false)}><X className="h-5 w-5" /></button>
          </div>
          <ul className="space-y-0.5 p-4 lg:p-0">
            {SECTIONS.map(({ id, label, icon: Icon }) => (
              <li key={id}>
                <button
                  onClick={() => scrollTo(id)}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-sm text-left transition-colors ${
                    activeSection === id
                      ? 'bg-indigo-50 text-[#6366F1] font-semibold'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                  {label}
                </button>
              </li>
            ))}
          </ul>
        </nav>
        {navOpen && <div className="lg:hidden fixed inset-0 z-30 bg-black/40" onClick={() => setNavOpen(false)} />}

        {/* Main content */}
        <main className="flex-1 min-w-0 max-w-3xl space-y-12 pb-20">
          <Section id="introduction" title="Introduction">
            <p>
              The BrixLead Public API lets you create and manage leads programmatically,
              and BrixLead Webhooks notify your system in real-time when leads change.
              Together they make it easy to plug BrixLead into your website forms,
              automation tools, custom CRMs, or notification systems.
            </p>
            <p>
              The API is REST-based, returns JSON, and uses standard HTTP status codes.
              All requests must be made over HTTPS. The base URL is:
            </p>
            <CodeBlock lang="text" copyable code="https://api.brixlead.com/api/v1" />
            <Callout tone="info">
              The <code>/v1</code> in the URL is the API version. Future breaking changes
              will be released as <code>/v2</code> while <code>/v1</code> stays stable —
              your integration won't suddenly break.
            </Callout>
          </Section>

          <Section id="authentication" title="Authentication">
            <p>
              Every Public API request must include an API key. Create one in the
              dashboard under <strong>Administrator → Developer → API Keys</strong>.
              The full key is shown <strong>once</strong> at creation — store it
              somewhere safe (a password manager or your CI/CD secret store).
            </p>

            <h3 className="text-base font-semibold text-slate-900 mt-6 mb-2">Sending the key</h3>
            <p>Include the key in the <code>Authorization</code> header using the Bearer scheme:</p>
            <CodeTabs lang={lang} setLang={setLang} examples={{
              curl: `curl https://api.brixlead.com/api/v1/leads \\
  -H "Authorization: Bearer brix_live_a1b2c3d4..."`,
              node: `import axios from 'axios';

const client = axios.create({
  baseURL: 'https://api.brixlead.com/api/v1',
  headers: { Authorization: \`Bearer \${process.env.BRIXLEAD_API_KEY}\` },
});`,
              python: `import requests

headers = {"Authorization": f"Bearer {os.environ['BRIXLEAD_API_KEY']}"}
res = requests.get("https://api.brixlead.com/api/v1/leads", headers=headers)`,
            }} />

            <p>The header <code>x-api-key: brix_live_...</code> also works if your tool can't set Authorization.</p>

            <Callout tone="warn">
              <strong>Never put your API key in client-side code.</strong> The key has full
              tenant access. Always call BrixLead from your server, not directly from a
              browser or mobile app.
            </Callout>

            <h3 className="text-base font-semibold text-slate-900 mt-6 mb-2">Revoking a key</h3>
            <p>
              In the dashboard, hit the trash icon next to a key. Calls using that key
              start returning <code>401 Unauthorized</code> immediately. Past usage stays
              recorded in the Audit Log for security review.
            </p>
          </Section>

          <Section id="rate-limits" title="Rate Limits">
            <p>
              The Public API is rate-limited to <strong>60 requests per minute per IP</strong>.
              When you exceed the limit, requests return <code>429 Too Many Requests</code>.
              Back off and retry after the limit resets (1 minute window).
            </p>
            <p>
              For higher volume (CRM sync, bulk import), batch your work or contact us
              for a tenant-scoped higher limit.
            </p>
          </Section>

          <Section id="endpoints" title="API Endpoints">
            <h3 className="text-base font-semibold text-slate-900 mt-2 mb-2">Create a lead</h3>
            <EndpointBadge method="POST" path="/leads" />
            <p>Creates a new lead. Goes through the same pipeline as the dashboard:</p>
            <ul className="list-disc pl-5 space-y-1 text-sm">
              <li>Duplicate auto-merge (if your tenant has it enabled — default ON)</li>
              <li>Auto-assignment via your round-robin rules</li>
              <li>Score calculation based on contact info + source</li>
              <li>Configured automations (welcome email, WhatsApp, etc.)</li>
              <li>A <code>lead.created</code> webhook event for any active subscriptions</li>
            </ul>

            <CodeTabs lang={lang} setLang={setLang} examples={{
              curl: `curl -X POST https://api.brixlead.com/api/v1/leads \\
  -H "Authorization: Bearer brix_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Rahul Sharma",
    "phones": ["+919876543210"],
    "emails": ["rahul@example.com"],
    "source": "Website",
    "campaignName": "Diwali Sale",
    "utmSource": "google",
    "utmCampaign": "diwali-2026",
    "country": "India",
    "state": "Maharashtra",
    "city": "Mumbai",
    "remark": "Interested in Diwali offer, call after 6pm"
  }'`,
              node: `const { data } = await client.post('/leads', {
  name: 'Rahul Sharma',
  phones: ['+919876543210'],
  emails: ['rahul@example.com'],
  source: 'Website',
  campaignName: 'Diwali Sale',
  utmSource: 'google',
  country: 'India',
  state: 'Maharashtra',
  city: 'Mumbai',
  remark: 'Interested in Diwali offer, call after 6pm',
});

console.log(data.lead.id, '— merged?', data.merged);`,
              python: `payload = {
    "name": "Rahul Sharma",
    "phones": ["+919876543210"],
    "emails": ["rahul@example.com"],
    "source": "Website",
    "campaignName": "Diwali Sale",
    "country": "India",
    "state": "Maharashtra",
    "city": "Mumbai",
    "remark": "Interested in Diwali offer, call after 6pm",
}
res = requests.post(f"{BASE}/leads", json=payload, headers=headers)
data = res.json()
print(data["lead"]["id"], "merged?", data["merged"])`,
            }} />

            <h4 className="text-sm font-semibold text-slate-700 mt-3 mb-1">Response</h4>
            <CodeBlock lang="json" copyable code={`{
  "lead": {
    "id": "ckxyz...",
    "name": "Rahul Sharma",
    "status": "new",
    "score": 65,
    "phones": [{ "isdCode": "+91", "phoneNumber": "9876543210", "isPrimary": true, "isVerified": false }],
    "emails": [{ "email": "rahul@example.com", "isPrimary": true, "isVerified": false }],
    "assignedTo": { "id": "ck...", "name": "Priya" },
    "createdAt": "2026-04-27T13:45:00.000Z"
  },
  "merged": false
}`} />

            <Callout tone="info">
              When the incoming lead matches an existing one by phone or email,
              <code> merged: true</code> is returned along with{' '}
              <code>masterLeadId</code> — the lead row that the duplicate was
              merged into. Use that ID instead of the one you submitted.
            </Callout>

            <h3 className="text-base font-semibold text-slate-900 mt-8 mb-2">List leads</h3>
            <EndpointBadge method="GET" path="/leads" />
            <p>Paginated list. Default 25 per page, max 100.</p>
            <CodeBlock lang="text" copyable code="GET /api/v1/leads?status=new&page=1&limit=25" />
            <h4 className="text-sm font-semibold text-slate-700 mt-3 mb-1">Common query params</h4>
            <ParamTable params={[
              ['status',       'string',  'Filter by status (new, contacted, etc.)'],
              ['source',       'string',  'Filter by source'],
              ['assignedToId', 'string',  'Filter by assigned user id'],
              ['search',       'string',  'Free-text search across name, phone, email, city'],
              ['dateFrom',     'string',  'YYYY-MM-DD — created on or after'],
              ['dateTo',       'string',  'YYYY-MM-DD — created on or before'],
              ['page',         'number',  'Page number, 1-indexed'],
              ['limit',        'number',  'Per page (1-100)'],
              ['sortBy',       'string',  'Field to sort by (createdAt, score, etc.)'],
              ['sortOrder',    'string',  'asc | desc'],
            ]} />

            <h3 className="text-base font-semibold text-slate-900 mt-8 mb-2">Get a single lead</h3>
            <EndpointBadge method="GET" path="/leads/:id" />
            <p>Returns the full lead with phones, emails, custom fields, and recent activities.</p>
            <CodeBlock lang="text" copyable code="GET /api/v1/leads/ckxyz..." />
          </Section>

          <Section id="lead-object" title="The Lead Object">
            <p>Every API response that returns a lead uses this shape:</p>
            <CodeBlock lang="json" copyable code={`{
  "id": "string (cuid)",
  "name": "string",
  "firstName": "string | null",
  "lastName": "string | null",
  "phones": [
    { "isdCode": "string | null (e.g. \\"+91\\")", "phoneNumber": "string | null",
      "isPrimary": "boolean", "isVerified": "boolean" }
  ],
  "emails": [
    { "email": "string",
      "isPrimary": "boolean", "isVerified": "boolean" }
  ],
  "status": "string",
  "source": "string | null",
  "score": "number (0-100)",
  "priority": "string | null",
  "country": "string | null",
  "state": "string | null",
  "city": "string | null",
  "pincode": "string | null",
  "address": "string | null",
  "notes": "string | null",
  "campaignName": "string | null",
  "utmSource": "string | null",
  "utmMedium": "string | null",
  "utmCampaign": "string | null",
  "hashtags": ["string"],
  "customFields": { "<fieldId>": "value" },
  "assignedToId": "string | null",
  "assignedTo": { "id": "string", "name": "string", "email": "string" } | null,
  "duplicateCount": "number",
  "createdAt": "ISO 8601",
  "updatedAt": "ISO 8601"
}`} />
            <Callout tone="info">
              <code>phones</code> and <code>emails</code> are arrays of objects, not
              strings. The first item is always primary (sorted server-side). Each
              phone exposes <code>isdCode</code> (dialling code) and{' '}
              <code>phoneNumber</code> (national number) as separate fields. When
              creating/updating, send phones as <code>{'{ isdCode, phoneNumber }'}</code>{' '}
              objects (plain strings like <code>"+91 9876543210"</code> are also accepted).
            </Callout>
          </Section>

          <Section id="webhooks" title="Webhooks">
            <p>
              Webhooks let your server receive realtime events when leads change in
              BrixLead — perfect for syncing to your own CRM, posting to Slack,
              triggering email campaigns, or anything else event-driven.
            </p>
            <h3 className="text-base font-semibold text-slate-900 mt-6 mb-2">Setup</h3>
            <ol className="list-decimal pl-5 space-y-1 text-sm">
              <li>Dashboard → <strong>Administrator → Developer → Webhooks</strong></li>
              <li>Click <strong>New Webhook</strong>, paste your endpoint URL</li>
              <li>Select the events you want to receive</li>
              <li>Save — your <strong>signing secret</strong> is shown once. Copy and store it.</li>
            </ol>
            <h3 className="text-base font-semibold text-slate-900 mt-6 mb-2">Receiving</h3>
            <p>BrixLead sends a <code>POST</code> to your URL with this shape:</p>
            <CodeBlock lang="json" copyable code={`{
  "event": "lead.created",
  "tenantId": "ck...",
  "occurredAt": "2026-04-27T13:45:00.000Z",
  "data": { /* event-specific payload, see below */ }
}`} />
            <h4 className="text-sm font-semibold text-slate-700 mt-3 mb-1">Headers BrixLead sends</h4>
            <ParamTable params={[
              ['Content-Type',          'string', 'application/json'],
              ['User-Agent',            'string', 'BrixLead-Webhooks/1.0'],
              ['X-BrixLead-Event',      'string', 'The event name, e.g. lead.created'],
              ['X-BrixLead-Signature',  'string', 'sha256=<hmac of body> — verify before trusting'],
            ]} />
            <h4 className="text-sm font-semibold text-slate-700 mt-3 mb-1">Responding</h4>
            <p>
              Return any <code>2xx</code> status code within <strong>10 seconds</strong>{' '}
              to acknowledge receipt. Anything else is treated as a failure and BrixLead
              will retry (see Retry Policy below). Keep your handler fast — process
              in a background job if heavy lifting is required.
            </p>
          </Section>

          <Section id="webhook-events" title="Webhook Events">
            <h3 className="text-base font-semibold text-slate-900 mt-2 mb-2">lead.created</h3>
            <p>Fired when a new lead is created from any source (dashboard, CSV import, public API, integrations).</p>
            <CodeBlock lang="json" copyable code={`{
  "event": "lead.created",
  "data": { "lead": { /* full Lead object */ } }
}`} />

            <h3 className="text-base font-semibold text-slate-900 mt-6 mb-2">lead.updated</h3>
            <p>Fired when any meaningful field on a lead changes. <code>changes</code> shows what moved.</p>
            <CodeBlock lang="json" copyable code={`{
  "event": "lead.updated",
  "data": {
    "lead": { /* full Lead object after the change */ },
    "changes": {
      "status":   { "from": "new",     "to": "contacted" },
      "priority": { "from": null,      "to": "high" }
    }
  }
}`} />

            <h3 className="text-base font-semibold text-slate-900 mt-6 mb-2">lead.merged</h3>
            <p>Fired when an incoming lead was auto-merged into an existing master.</p>
            <CodeBlock lang="json" copyable code={`{
  "event": "lead.merged",
  "data": {
    "master":          { /* full Lead object — the canonical record */ },
    "duplicateLeadId": "ck...",
    "mergeSource":     "manual_form" | "csv_import" | "api" | "webhook"
  }
}`} />
            <Callout tone="info">
              On the receiving side: refresh your local copy of the master lead. The
              duplicate row exists for audit but is hidden from normal lead lists in
              BrixLead — don't show it as a separate contact in your system.
            </Callout>

            <h3 className="text-base font-semibold text-slate-900 mt-6 mb-2">lead.deleted</h3>
            <p>Fired on soft-delete (lead moved to trash).</p>
            <CodeBlock lang="json" copyable code={`{
  "event": "lead.deleted",
  "data": { "leadId": "ck...", "name": "Rahul Sharma" }
}`} />
          </Section>

          <Section id="webhook-verify" title="Verifying Signatures">
            <p>
              Every webhook includes an <code>X-BrixLead-Signature</code> header.
              Always verify before trusting the request — without this check, anyone
              who finds your URL can post fake events.
            </p>
            <CodeTabs lang={lang} setLang={setLang} examples={{
              curl: `# Manual verification — most users will use a code library, not curl.
# Header value:  X-BrixLead-Signature: sha256=<hex>
# Compute:       hmac_sha256(your_secret, request_body) — compare hex.`,
              node: `import crypto from 'crypto';
import express from 'express';

const app = express();
const SECRET = process.env.BRIXLEAD_WEBHOOK_SECRET;

// IMPORTANT: capture the raw body BEFORE JSON parse so the signature
// matches exactly what BrixLead sent.
app.post('/webhooks/brixlead',
  express.raw({ type: 'application/json' }),
  (req, res) => {
    const sig = req.headers['x-brixlead-signature'];
    const body = req.body.toString();

    const expected = 'sha256=' + crypto
      .createHmac('sha256', SECRET)
      .update(body)
      .digest('hex');

    // Constant-time compare to avoid timing attacks.
    const a = Buffer.from(expected);
    const b = Buffer.from(String(sig));
    if (a.length !== b.length || !crypto.timingSafeEqual(a, b)) {
      return res.status(401).send('Invalid signature');
    }

    const event = JSON.parse(body);
    console.log(event.event, event.data);
    res.status(200).send('ok');
  });`,
              python: `import hmac, hashlib, os
from flask import Flask, request, abort

app = Flask(__name__)
SECRET = os.environ["BRIXLEAD_WEBHOOK_SECRET"].encode()

@app.post("/webhooks/brixlead")
def receive():
    body = request.get_data()
    sig  = request.headers.get("X-BrixLead-Signature", "")
    expected = "sha256=" + hmac.new(SECRET, body, hashlib.sha256).hexdigest()
    if not hmac.compare_digest(sig, expected):
        abort(401)

    event = request.get_json()
    print(event["event"], event["data"])
    return "ok", 200`,
            }} />
            <Callout tone="warn">
              Use a <strong>constant-time comparison</strong> (<code>crypto.timingSafeEqual</code>{' '}
              in Node, <code>hmac.compare_digest</code> in Python). Plain <code>==</code> leaks
              timing information that can be used to forge signatures.
            </Callout>
          </Section>

          <Section id="webhook-retries" title="Retry Policy">
            <p>BrixLead retries failed webhook deliveries on this schedule:</p>
            <ul className="list-disc pl-5 text-sm space-y-1">
              <li><strong>Attempt 1</strong> — immediate</li>
              <li><strong>Attempt 2</strong> — 30 seconds later</li>
              <li><strong>Attempt 3</strong> — 5 minutes later</li>
            </ul>
            <p className="mt-3">A delivery is considered <strong>failed</strong> if:</p>
            <ul className="list-disc pl-5 text-sm space-y-1">
              <li>Your endpoint returns a 5xx status code</li>
              <li>Your endpoint returns 408 (request timeout) or 429 (rate limited)</li>
              <li>The request times out after 10 seconds</li>
              <li>A network error occurs</li>
            </ul>
            <p className="mt-3">
              4xx responses other than 408/429 are treated as <strong>permanent failures</strong> —
              we don't retry because your endpoint has explicitly rejected the payload.
            </p>
            <p className="mt-3">
              Your delivery history is visible in the dashboard under{' '}
              <strong>Developer → Webhooks → eye icon → Recent Deliveries</strong> with
              status, attempts, and error messages for the last 20 attempts.
            </p>
            <Callout tone="info">
              <strong>Idempotency tip:</strong> webhooks may occasionally arrive more
              than once (network hiccups, retries from previous failures). Use the
              lead's <code>id</code> as the idempotency key in your handler.
            </Callout>
          </Section>

          <Section id="errors" title="Errors">
            <p>BrixLead uses standard HTTP status codes:</p>
            <ParamTable params={[
              ['200',  'OK',                   'Request succeeded'],
              ['201',  'Created',              'Resource created'],
              ['400',  'Bad Request',          'Invalid request body or query parameters'],
              ['401',  'Unauthorized',         'Missing, invalid, or revoked API key'],
              ['403',  'Forbidden',            'Plan limit reached or insufficient permissions'],
              ['404',  'Not Found',            'Lead/resource does not exist for your tenant'],
              ['429',  'Too Many Requests',    'Rate limit exceeded — retry after 60s'],
              ['500',  'Internal Server Error', 'Something went wrong on our end — retry'],
            ]} />
            <h4 className="text-sm font-semibold text-slate-700 mt-4 mb-1">Error response shape</h4>
            <CodeBlock lang="json" copyable code={`{
  "statusCode": 400,
  "message": "phones must be an array of strings",
  "error": "Bad Request"
}`} />
            <p>
              For validation errors (<code>400</code>), <code>message</code> may be an
              array of strings — one per failed field.
            </p>
          </Section>

          <Section id="changelog" title="Changelog">
            <ul className="text-sm space-y-2">
              <li>
                <span className="text-slate-400 font-mono">2026-04-27</span> — v1.0
                Initial public release. Lead create / read / list, four webhook events
                with signed delivery and retries.
              </li>
            </ul>
            <p className="text-sm text-slate-500 mt-6">
              Questions or feedback? Email{' '}
              <a href="mailto:support@brixlead.com" className="text-[#6366F1] hover:underline">
                support@brixlead.com
              </a>{' '}
              or open an issue on our repo.
            </p>
          </Section>
        </main>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Building blocks — kept inline so the docs file is self-contained.
// ────────────────────────────────────────────────────────────────────────────

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-20">
      <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">{title}</h2>
      <div className="prose prose-sm max-w-none text-slate-700 space-y-3 [&_code]:bg-slate-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-[13px] [&_code]:font-mono [&_code]:text-[#6366F1]">
        {children}
      </div>
    </section>
  );
}

function CodeBlock({ lang = 'text', code, copyable = false }: { lang?: string; code: string; copyable?: boolean }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="relative group">
      <pre className="bg-slate-900 text-slate-100 rounded-lg p-4 text-xs overflow-x-auto leading-relaxed">
        <code>{code}</code>
      </pre>
      {copyable && (
        <button
          onClick={handleCopy}
          className="absolute top-2 right-2 p-1.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity"
          title={copied ? 'Copied!' : 'Copy'}
        >
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      )}
      {lang !== 'text' && (
        <span className="absolute top-2 left-3 text-[10px] text-slate-500 uppercase font-semibold">{lang}</span>
      )}
    </div>
  );
}

function CodeTabs({
  lang,
  setLang,
  examples,
}: {
  lang: Lang;
  setLang: (l: Lang) => void;
  examples: Record<Lang, string>;
}) {
  const langs: { id: Lang; label: string }[] = [
    { id: 'curl',   label: 'cURL' },
    { id: 'node',   label: 'Node.js' },
    { id: 'python', label: 'Python' },
  ];
  return (
    <div>
      <div className="flex gap-1 mb-2">
        {langs.map((l) => (
          <button
            key={l.id}
            onClick={() => setLang(l.id)}
            className={`px-3 py-1 text-xs font-medium rounded-t ${
              lang === l.id
                ? 'bg-slate-900 text-white'
                : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
            }`}
          >
            {l.label}
          </button>
        ))}
      </div>
      <CodeBlock lang={lang} code={examples[lang]} copyable />
    </div>
  );
}

function EndpointBadge({ method, path }: { method: 'GET' | 'POST' | 'PATCH' | 'DELETE'; path: string }) {
  const tone =
    method === 'GET'    ? 'bg-blue-50 text-blue-700' :
    method === 'POST'   ? 'bg-emerald-50 text-emerald-700' :
    method === 'PATCH'  ? 'bg-amber-50 text-amber-700' :
                          'bg-red-50 text-red-700';
  return (
    <div className="inline-flex items-center gap-2 mb-3 text-sm font-mono">
      <span className={`px-2 py-0.5 rounded font-semibold text-xs ${tone}`}>{method}</span>
      <code className="text-slate-700 font-mono">{path}</code>
    </div>
  );
}

function ParamTable({ params }: { params: [string, string, string][] }) {
  return (
    <div className="overflow-x-auto">
      <table className="text-sm w-full border border-slate-200 rounded-lg overflow-hidden">
        <thead className="bg-slate-100">
          <tr className="border-b-2 border-slate-200">
            <th className="px-3 py-2 text-left text-[10px] font-bold text-slate-700 uppercase tracking-wider">Name</th>
            <th className="px-3 py-2 text-left text-[10px] font-bold text-slate-700 uppercase tracking-wider">Type</th>
            <th className="px-3 py-2 text-left text-[10px] font-bold text-slate-700 uppercase tracking-wider">Description</th>
          </tr>
        </thead>
        <tbody>
          {params.map(([name, type, desc]) => (
            <tr key={name} className="border-t border-slate-100">
              <td className="px-3 py-2 font-mono text-xs text-[#6366F1]">{name}</td>
              <td className="px-3 py-2 font-mono text-xs text-slate-500">{type}</td>
              <td className="px-3 py-2 text-slate-600">{desc}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Callout({ tone, children }: { tone: 'info' | 'warn'; children: React.ReactNode }) {
  const cls =
    tone === 'info'
      ? 'bg-indigo-50 border-l-4 border-indigo-400 text-indigo-900'
      : 'bg-amber-50 border-l-4 border-amber-400 text-amber-900';
  return (
    <div className={`${cls} rounded-r-lg px-4 py-3 text-sm [&_code]:bg-white/50 [&_code]:text-current`}>
      {children}
    </div>
  );
}
